import { Children, useEffect, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import HeroImageCropper from "./HeroImageCropper";
import HeroStatsEditor from "./HeroStatsEditor";
import AdvancedSchemaEditor from "./components/SchemaEditor";
import Editor from "../../../components/RichTextEditor/Editor"
import {
  IMAGE_SPECS,
  validateImageBeforeUpload,
} from "../home/Validation";
const INPUT = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: 8,
  fontSize: 14,
  color: "#1e293b",
  background: "#fff"
};
const defaults = {
  hero: {
    badge: "Customer Success Stories",
    heading: {
      line1: "Real Procurement Challenges.",
      line2: "Proven Business Results."
    },
    description: "",
    stats: [
      { value: "35%", label: "Avg. Cost Reduction" },
      { value: "80%", label: "Avg. Faster Procurement" },
      { value: "15K+", label: "Avg. Hours Saved" }
    ]
  },
  business_impact: {
    spend_managed_value: "2Cr",
    average_savings_value: "35%",
    faster_cycle_value: "80%",
    customer_satisfaction_value: "95%"
  },
  team_statement: {
    text: "",
    author: "Team PROCMAN"
  },
  seo_content: "",
  footer_cta: {
    heading: "Ready to Transform Your Procurement Process?",
    subheading: ""
  },
  faqs: [],
  meta: {
    title: "",
    description: "",
    keywords: "",
    canonical_url: "https://procman.ai/case-studies",
    og_image: "",
    og_title: "",
    og_description: "",
    twitter_title: "",
    twitter_description: "",
    robots_index: true,
    twitter_card: "summary_large_image"
  },
  schemas: []
};
const merge = (value = {}) => ({
  ...defaults,
  ...value,
  hero: {
    ...defaults.hero,
    ...(value.hero || {}),
    heading: {
      ...defaults.hero.heading,
      ...(value.hero?.heading || {})
    }
  },
  business_impact: {
    ...defaults.business_impact,
    ...(value.business_impact || {})
  },
  team_statement: {
    ...defaults.team_statement,
    ...(value.team_statement || {})
  },
  footer_cta: {
    ...defaults.footer_cta,
    ...(value.footer_cta || {})
  },
  meta: {
    ...defaults.meta,
    ...(value.meta || {}),
    canonical_url: value.meta?.canonical_url || defaults.meta.canonical_url,
    og_title: value.meta?.og_title || value.meta?.title || "",
    og_description: value.meta?.og_description || value.meta?.description || "",
    twitter_title: value.meta?.twitter_title || value.meta?.og_title || value.meta?.title || "",
    twitter_description: value.meta?.twitter_description || value.meta?.og_description || value.meta?.description || ""
  },
  faqs: value.faqs || [],
  schemas: value.schemas || []
});
const formatLabel = label => String(label).replace(/\b\w/g, character => character.toUpperCase()).replace(/\bOg\b/g, "OG").replace(/\bUrl\b/g, "URL").replace(/\bSeo\b/g, "SEO");
function Field({
  label,
  children,
  style
}) {
  return <label style={{
    display: "block",
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    ...style
  }}>{formatLabel(label)}<div style={{
    marginTop: 5,
    fontWeight: 400
  }}>{children}</div></label>;
}
function Card({
  title,
  children
}) {
  const order = {
    "SEO & Social Sharing": 1,
    "Schema.org Structured Data": 2,
    Hero: 3,
    "Business Impact": 6,
    "Feedback Statement": 7,
    "SEO Content": 8,
    "Frequently Asked Questions": 9,
    "Closing CTA": 10
  }[title];
  const section = (heading, body, sectionOrder) => <section ref={node => {
    if (node?.parentElement) {
      node.parentElement.style.display = "flex";
      node.parentElement.style.flexDirection = "column";
    }
  }} style={{
    order: sectionOrder,
    width: "100%",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "20px 22px",
    marginBottom: 18,
    boxShadow: "0 1px 4px rgba(0,0,0,.05)"
  }}><h2 style={{
    margin: "0 0 16px",
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a"
  }}>{heading}</h2>{body}</section>;
  if (title === "Hero") {
    const [backgroundImage, heroMetrics, ...heroFields] = Children.toArray(children);
    return <>{section("Hero Section", heroFields, 3)}{section("Hero Metrics", heroMetrics, 4)}{section("Background Image", backgroundImage, 5)}</>;
  }
  return section(title === "SEO & Social Sharing" ? "SEO Data" : title === "Schema.org Structured Data" ? "Schema" : title, children, order);
}
function Spinner() {
  return <span style={{
    width: 13,
    height: 13,
    border: "2px solid rgba(255,255,255,.5)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "csSpin .7s linear infinite"
  }} />;
}


function ImageUploadField({ label, fieldName, value, defaultValue, defaultNote, disabled, uploading, onUpload, onPreview, error, setError }) {
  const [localPreview, setLocalPreview] = useState(null);
  const spec = IMAGE_SPECS?.[fieldName];
  const isCustom = Boolean(localPreview || value);
  const previewUrl = localPreview || value || defaultValue;

  const handleFile = async (file) => {
    if (!file) return;
    if (spec) {
      const problems = await validateImageBeforeUpload(file, fieldName);
      if (problems.length) {
        setError?.(problems.join(" "));
        return;
      }
    }
    setError?.(null);
    setLocalPreview(URL.createObjectURL(file));
    onUpload(fieldName, file);
  };

  return (
    <div style={{ marginBottom: 18, background: "#f8fafc", padding: 14, borderRadius: 10, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 16, alignItems: "center" }}>
        <div style={{ height: 80, width: 130, borderRadius: 8, border: `1.5px dashed ${error ? "#fca5a5" : "#cbd5e1"}`, background: "#fff", overflow: "hidden", display: "grid", placeItems: "center" }}>
          {previewUrl ? (
            <button type="button" aria-label={`Preview ${label}`} onClick={() => onPreview?.(previewUrl)}
              style={{ padding: 0, margin: 0, border: 0, background: "none", width: "100%", height: "100%", cursor: "zoom-in" }}>
              <img src={previewUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ) : (
            <span style={{ color: "#64748b", fontSize: 11, textAlign: "center", padding: 6, fontWeight: 500 }}>No image</span>
          )}
        </div>
        <div>
          <input
            id={`img-${fieldName}`}
            type="file"
            accept={spec ? spec.formats.join(",") : "image/png,image/jpeg,image/webp"}
            disabled={disabled || uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              handleFile(file);
            }}
            style={{ display: "none" }}
          />
          <label
            htmlFor={`img-${fieldName}`}
            style={{
              display: "inline-block", border: "1px solid #bfdbfe", borderRadius: 6, padding: "8px 14px",
              background: "#eff6ff", color: "#2563eb", fontSize: 13, fontWeight: 700,
              cursor: disabled || uploading ? "not-allowed" : "pointer",
              opacity: disabled || uploading ? 0.55 : 1,
            }}
          >
            {uploading ? "Uploading…" : isCustom ? "Replace image" : "Upload image"}
          </label>
          {spec?.help && !error && <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12 }}>{spec.help}</p>}
          {!isCustom && previewUrl && defaultNote && !error && <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12 }}>{defaultNote}</p>}
          {error && <p style={{ margin: "8px 0 0", color: "#e11d48", fontSize: 12, fontWeight: 600 }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}



const SchemaEditor = AdvancedSchemaEditor;
export default function CaseStudyPageMaster() {
  const [content, setContent] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [record, setRecord] = useState(null);
  const [uploadingImages, setUploadingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [, setOverlayUrl] = useState("");

  useEffect(() => {
    axiosInstance.get("/api/superadmin/case-studies-page/").then(({
      data
    }) => setContent(merge((data?.data ?? data)?.content))).catch(() => setNotice("Could not load page content.")).finally(() => setLoading(false));
  }, []);
  const set = (group, key, value) => setContent(state => {
    const next = { ...state[group], [key]: value };
    if (group === "meta" && key === "title") {
      next.og_title = value;
      next.twitter_title = value;
    }
    if (group === "meta" && key === "description") {
      next.og_description = value;
      next.twitter_description = value;
    }
    return { ...state, [group]: next };
  });

  useEffect(() => {
    axiosInstance.get("/api/superadmin/case-studies-page/")
      .then(({ data }) => {
        const row = data?.data ?? data;
        setRecord(row);
        setContent(merge(row?.content));
      })
      .catch(() => setNotice("Could not load page content."))
      .finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    setNotice("");
    try {
      await axiosInstance.put("/api/superadmin/case-studies-page/1/", {
        content
      });
      setNotice("Case studies page saved successfully.");
    } catch {
      setNotice("Could not save page content.");
    } finally {
      setSaving(false);
    }
  };
  if (loading) return <div style={{
    minHeight: "100vh",
    background: "#f8fafc",
    display: "grid",
    placeItems: "center",
    color: "#64748b"
  }}>Loading Case Studies CMS...</div>;



  const uploadImage = async (fieldName, file) => {
    if (!record?.id) {
      setNotice("Could not upload: page record not found.");
      return;
    }
    setUploadingImages((s) => ({ ...s, [fieldName]: true }));
    try {
      const body = new FormData();
      body.append(fieldName, file);
      await axiosInstance.put(`/api/superadmin/case-studies-page/${record.id}/`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await axiosInstance.get("/api/superadmin/case-studies-page/");
      const row = data?.data ?? data;
      setRecord(row);
      setNotice(`${formatLabel(fieldName.replace(/_/g, " "))} uploaded successfully.`);
    } catch (error) {
      setNotice(error?.response?.data?.message || error?.response?.data?.detail || "Could not upload image.");
    } finally {
      setUploadingImages((s) => ({ ...s, [fieldName]: false }));
    }
  };

  const setImageError = (fieldName, message) =>
    setImageErrors((prev) => {
      const next = { ...prev };
      if (message) next[fieldName] = message;
      else delete next[fieldName];
      return next;
    });

  const imageProps = (fieldName) => ({
    fieldName,
    value: record?.[fieldName],
    disabled: !record?.id,
    uploading: Boolean(uploadingImages[fieldName]),
    onUpload: uploadImage,
    onPreview: setOverlayUrl,
    error: imageErrors[fieldName],
    setError: (m) => setImageError(fieldName, m),
  });
  return <div style={{
    minHeight: "100vh",
    padding: "28px 30px",
    background: "#f8fafc",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#1e293b"
  }}><style>{`@keyframes csSpin{to{transform:rotate(360deg)}}`}</style><div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 24,
    flexWrap: "wrap"
  }}><div><h1 style={{
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#0f172a"
  }}>Case Studies Page</h1><p style={{
    margin: "5px 0 0",
    fontSize: 13,
    color: "#64748b"
  }}>Manage the public case studies page content and SEO.</p></div><button disabled={saving} onClick={save} style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    position: "fixed",
    top: 88,
    right: 48,
    zIndex: 1200,
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: saving ? "wait" : "pointer",
    opacity: saving ? .7 : 1,
    boxShadow: "0 6px 16px rgba(37,99,235,.3)"
  }}>{saving && <Spinner />}{saving ? "Saving..." : "Save Data"}</button></div>{notice && <div style={{
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 13,
    background: notice.includes("Could") ? "#fef2f2" : "#f0fdf4",
    color: notice.includes("Could") ? "#b91c1c" : "#166534",
    border: `1px solid ${notice.includes("Could") ? "#fecaca" : "#bbf7d0"}`
  }}>{notice}</div>}<Card title="Hero"><HeroImageCropper /><HeroStatsEditor stats={content.hero.stats} onChange={stats => setContent(s => ({ ...s, hero: { ...s.hero, stats } }))} /><Field label="Badge"><input style={INPUT} value={content.hero.badge} onChange={e => set("hero", "badge", e.target.value)} /></Field><div style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12
  }}><Field label="Heading" style={{ gridColumn: "1 / -1" }}><input aria-label="Heading" placeholder="Enter hero heading" style={INPUT} value={[content.hero.heading.line1, content.hero.heading.line2].filter(Boolean).join(" ")} onChange={e => setContent(s => ({
    ...s,
    hero: {
      ...s.hero,
      heading: {
        ...s.hero.heading,
        line1: e.target.value,
        line2: ""
      }
    }
  }))} />
    </Field>
  </div>
      <Field label="Description">
        <textarea rows="4" style={INPUT} value={content.hero.description} onChange={e => set("hero", "description", e.target.value)} />
      </Field>
    </Card>
    <Card title="Business Impact">
      {Object.entries(content.business_impact).map(([key, value]) =>
        <Field key={key} label={key.replace(/_/g, " ")}>
          <input style={INPUT} value={value} onChange={e => set("business_impact", key, e.target.value)} />
        </Field>)}
    </Card>

    <Card title="Feedback Statement">
      <Field label="Statement">
        <textarea rows="4" style={INPUT} value={content.team_statement.text} onChange={e => set("team_statement", "text", e.target.value)} />
      </Field>
      <Field label="Author">
        <input style={INPUT} value={content.team_statement.author} onChange={e => set("team_statement", "author", e.target.value)} />
      </Field>
    </Card>
    {/* <Card title="SEO Content">
      <Field label="SEO Content"> */}
    {/* <RichTextEditor
        value={content.seo_content || ""}
        onChange={seo_content => setContent(s => ({ ...s, seo_content }))}
        placeholder="Write indexable SEO content for the Case Studies page..." minHeight={260}
      /> */}

    {/* <Editor
          value={content.seo_content || ""}
          onChange={seo_content => setContent(s => ({ ...s, seo_content }))}
          placeholder="Write indexable SEO content for the Case Studies page..." minHeight={260}
        />
      </Field>
      <p style={{ margin: "-4px 0 0", fontSize: 12, color: "#64748b" }}>Add up to 2,000?3,000 characters of useful page content. Links, tables, headings, lists, and basic formatting are supported.</p></Card><Card title="Frequently Asked Questions">{content.faqs.map((faq, index) => <div key={index} style={{
        padding: 14,
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        background: "#f8fafc",
        marginBottom: 10
      }}>
        <Field label="Question">
          <input style={INPUT} value={faq.question || ""} onChange={e => updateFaq(index, "question", e.target.value)} />
        </Field>
        <Field label="Answer">
          <textarea rows="3" style={INPUT} value={faq.answer || ""} onChange={e => updateFaq(index, "answer", e.target.value)} />
        </Field>
        <button onClick={() => setContent(s => ({
          ...s,
          faqs: s.faqs.filter((_, i) => i !== index)
        }))} style={{
          border: "1px solid #fecaca",
          borderRadius: 7,
          padding: "6px 10px",
          background: "#fff",
          color: "#dc2626"
        }}>Remove Question</button></div>)}<button onClick={() => setContent(s => ({
          ...s,
          faqs: [...s.faqs, {
            question: "",
            answer: ""
          }]
        }))} style={{
          border: "1px solid #bfdbfe",
          borderRadius: 7,
          padding: "7px 11px",
          background: "#eff6ff",
          color: "#2563eb",
          fontWeight: 600
        }}>+ Add Question</button>
    </Card> */}
    {/* <Card title="SEO & Social Sharing">
      {Object.entries(content.meta).map(([key, value]) => <Field key={key} label={key.replace(/_/g, " ")}>{key === "twitter_card" ? <select style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)}><option value="summary_large_image">Summary large image</option><option value="summary">Summary</option><option value="app">App</option><option value="player">Player</option></select> : ["description", "og_description"].includes(key) ? <textarea rows="3" style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)} /> : <input style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)} />}</Field>)}</Card><Card title="Schema.org Structured Data"><SchemaEditor schemas={content.schemas} onChange={schemas => setContent(s => ({
        ...s,
        schemas
      }))} 

      <div style={{ marginTop: 8 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Images</div>
    <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
      Uploads immediately as soon as a file is chosen. Twitter image falls back to the OG image if not uploaded.
    </p>
    <ImageUploadField label="OG Image" {...imageProps("og_image")} />
    <ImageUploadField
      label="Twitter Image"
      {...imageProps("twitter_image")}
      defaultValue={record?.og_image}
      defaultNote="Using the OG image by default"
    />
  


      
     </div>
    </Card> */}


    <Card title="SEO & Social Sharing">
      {/* image ab neeche upload field se aayegi */}
      {Object.entries(content.meta)
        .filter(([key]) => key !== "og_image")
        .map(([key, value]) => (
          <Field
            key={key}
            label={{ title: "Meta Title", description: "Meta Description", keywords: "Meta Keywords" }[key] || key.replace(/_/g, " ")}
          >
            {key === "robots_index" ? (
              <select
                style={INPUT}
                value={value === false ? "noindex" : "index"}
                onChange={e => set("meta", key, e.target.value === "index")}
              >
                <option value="index">index, follow</option>
                {/* <option value="noindex">noindex, nofollow</option> */}
              </select>
            ) : key === "twitter_card" ? (
              <select style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)}>
                <option value="summary_large_image">Summary large image</option>
                <option value="summary">Summary</option>
                <option value="app">App</option>
                <option value="player">Player</option>
              </select>
            ) : ["description", "og_description", "twitter_description"].includes(key) ? (
              <textarea rows="3" style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)} />
            ) : (
              <input style={INPUT} value={value} onChange={e => set("meta", key, e.target.value)} />
            )}
          </Field>
        ))}

      {/* ===== SEO CONTENT (moved here) ===== */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
          SEO Content
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
          Long-form SEO content with headings, lists, tables, links, and formatting.
        </p>
        <Editor
          value={content.seo_content || ""}
          onChange={seo_content => setContent(s => ({ ...s, seo_content }))}
          placeholder="Write indexable SEO content for the Case Studies page..."
          minHeight={260}
        />
      </div>

      {/* ===== IMAGES (moved from Schema card) ===== */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
          Images
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
          {/* Uploads immediately as soon as a file is chosen. Twitter image falls back to the OG image if not uploaded. */}
          Twitter Image Syncs with OG Image by Default.
          The Twitter image initially uses the OG image. Upload a separate Twitter image anytime to override it.
        </p>
        <ImageUploadField label="OG Image" {...imageProps("og_image")} />
        <ImageUploadField
          label="Twitter Image"
          {...imageProps("twitter_image")}
          defaultValue={record?.og_image}
          defaultNote="Using the OG image by default"
        />
      </div>
    </Card>


    {/* <Card title="Schema.org Structured Data">
      <SchemaEditor
        schemas={content.schemas}
        onChange={schemas =>
          setContent(s => ({
            ...s,
            schemas,
          }))
        }
      />

      <div style={{ marginTop: 8 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#334155",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 6,
          }}
        >
          Images
        </div>

        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
          Uploads immediately as soon as a file is chosen. Twitter image falls
          back to the OG image if not uploaded.
        </p>

        <ImageUploadField label="OG Image" {...imageProps("og_image")} />

        <ImageUploadField
          label="Twitter Image"
          {...imageProps("twitter_image")}
          defaultValue={record?.og_image}
          defaultNote="Using the OG image by default"
        />

      </div>
    </Card> */}

    <Card title="Schema.org Structured Data">
      <SchemaEditor
        schemas={content.schemas}
        onChange={schemas => setContent(s => ({ ...s, schemas }))}
      />
    </Card>
    <Card title="Closing CTA">
      <Field label="Heading">
        <input style={INPUT} value={content.footer_cta.heading} onChange={e => set("footer_cta", "heading", e.target.value)} />
      </Field>
      <Field label="Subheading">
        <textarea rows="3" style={INPUT} value={content.footer_cta.subheading} onChange={e => set("footer_cta", "subheading", e.target.value)} />
      </Field>
    </Card>
  </div>;
}
